import { styled, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { FC, SVGProps } from 'react';
import { Typography } from '../../../components/Typography/Typography';
import { WorkflowTimeline } from '../../../components/WorkflowStages/WorkflowTimeline';
import { Workflow } from '../../../types';
import { formatDate } from '../../../utils/formatters';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { ReactComponent as CalendarDisabledIcon } from '../../../assets/icons/calendar-disabled.svg';
import { ReactComponent as SickIcon } from '../../../assets/icons/emergency-home.svg';
import { ReactComponent as PauseIcon } from '../../../assets/icons/pause.svg';
import { ReactComponent as SettingsIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as AdministrationIcon } from '../../../assets/icons/administration.svg';
import { ReactComponent as ReportIcon } from '../../../assets/icons/report.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as ListIcon } from '../../../assets/icons/list-view.svg';

type Props = {
  workflow: Workflow;
};

// Whole panel cross-fades / slides in when the selected workflow changes.
const panelVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 320, damping: 30 } },
};

// Default rows are free-text names, so the icon is picked by keyword; anything unknown gets a list icon.
const ROW_ICONS: Array<[RegExp, FC<SVGProps<SVGSVGElement>>]> = [
  [/vacation|annual leave/i, CalendarIcon],
  [/holiday/i, CalendarDisabledIcon],
  [/sick|bereavement/i, SickIcon],
  [/garden leave/i, PauseIcon],
  [/restructuring/i, SettingsIcon],
  [/administrative/i, AdministrationIcon],
  [/marketing/i, ReportIcon],
  [/sourcing|diligence/i, SearchIcon],
];

const rowIcon = (row: string) => ROW_ICONS.find(([pattern]) => pattern.test(row))?.[1] ?? ListIcon;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Right-hand detail pane for the Workflows page: the selected workflow's name, a meta row (approval
 * stages, submitters, last edited), its approval pipeline as a stepper (Submitter -> approvers ->
 * Closed) and its leave & non-deal (default) rows. Purely presentational and read-only.
 */
export function WorkflowDetail({ workflow }: Props) {
  const { colors } = useTheme();
  const stages = workflow.approvalStages ?? [];
  const rows = workflow.defaultRows ?? [];

  // Approval stages = approver steps only (not the Submitter / Closed endpoints).
  const meta = [
    plural(stages.length, 'approval stage'),
    workflow.submitterCount != null ? plural(workflow.submitterCount, 'submitter') : null,
    workflow.updated_at ? `Last edited ${formatDate(workflow.updated_at)}` : null,
  ].filter(Boolean);

  return (
    <Panel
      key={workflow.id}
      variants={panelVariants}
      initial='hidden'
      animate='visible'
      style={{
        backgroundColor: colors.surfaceBackground.bg1,
        border: `1px solid ${colors.border.default}`,
      }}
    >
      <Header>
        <Typography variant='h2' color={colors.text.main} style={{ marginBottom: 0 }}>
          {workflow.name}
        </Typography>
        <MetaRow>
          {meta.map((item, index) => (
            <Typography key={item} variant='body' color={colors.text.secondary}>
              {index > 0 && <MetaDot style={{ backgroundColor: colors.text.caption }} />}
              {item}
            </Typography>
          ))}
        </MetaRow>
      </Header>

      <Section>
        <SectionLabel variant='overline' color={colors.text.caption} style={{ marginBottom: 0 }}>
          Approval pipeline
        </SectionLabel>
        <WorkflowTimeline stages={stages} animationKey={workflow.id} labels={workflow.stageLabels} />
      </Section>

      <Divider style={{ backgroundColor: colors.border.default }} />

      <Section>
        <SectionLabel variant='overline' color={colors.text.caption} style={{ marginBottom: 0 }}>
          Leave & non-deal rows
        </SectionLabel>
        {rows.length ? (
          <RowChips>
            {rows.map((row) => {
              const Icon = rowIcon(row);
              return (
                <RowChip
                  key={row}
                  style={{
                    backgroundColor: colors.surfaceBackground.bg2,
                    border: `1px solid ${colors.border.default}`,
                  }}
                  sx={{ svg: { path: { fill: colors.icon.default } } }}
                >
                  <Icon aria-hidden />
                  <Typography variant='body' color={colors.text.secondary}>
                    {row}
                  </Typography>
                </RowChip>
              );
            })}
          </RowChips>
        ) : (
          <Typography variant='body' color={colors.text.caption}>
            No leave or non-deal rows
          </Typography>
        )}
      </Section>
    </Panel>
  );
}

const Panel = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 36px;
  border-radius: 14px;
  min-height: 260px;
`;

const Header = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  p {
    display: inline-flex;
    align-items: center;
  }
`;

const MetaDot = styled('span')`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin: 0 10px;
`;

const Section = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionLabel = styled(Typography)`
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const Divider = styled('div')`
  height: 1px;
  width: 100%;
`;

const RowChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const RowChip = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  white-space: nowrap;
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;
